/**
 * Where a script-level error happened, as `url:line:col`, taken from the `ErrorEvent` the global
 * handler receives.
 *
 * This exists because of the one error class that arrives with **no stack at all**: a `SyntaxError`
 * raised while *parsing* a script. Nothing threw, so there are no frames — the engine failed before it
 * could run a single statement. Every locating mechanism we have reads `stack`, so such a report has
 * only ever told us that some script somewhere failed to parse, never which one.
 *
 * TEC-3747 is 320 occurrences of `SyntaxError: Unexpected identifier 'userceeen'` that could not be
 * attributed to any script for exactly this reason. `window.onerror` has carried the answer the whole
 * time in `filename`/`lineno`/`colno`; we simply never read them.
 *
 * Its own field rather than a synthesized stack frame, for the reason `RecordClientErrorInput.cause`
 * documents: `stack` is a fingerprint input, so writing into it re-tickets every already-open issue.
 * That also keeps this honest — a real stack and a position the browser handed us are different kinds
 * of evidence, and making them look alike would invite reading one as the other.
 */
/**
 * Browsers report 0 for "unknown" as readily as they report a real line, and a `0` in a ticket reads
 * like a position rather than like a gap. Only a positive integer is a position.
 */
function position(value: number | undefined): number | undefined {
	return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : undefined;
}

export function scriptErrorLocation(event: { filename?: string; lineno?: number; colno?: number }): string | undefined {
	const url = event.filename?.trim();
	// Empty on a cross-origin script, the same censoring that makes `event.error` null there. Absent
	// evidence rather than evidence of an inline script, so report nothing instead of a bare `:1:1`.
	if (!url) return undefined;

	const line = position(event.lineno);
	// A column with no line cannot be placed, so the URL alone is the honest answer — never `url::34`.
	if (line === undefined) return url;

	const column = position(event.colno);
	return column === undefined ? `${url}:${line}` : `${url}:${line}:${column}`;
}
